import React, { useCallback, useMemo, useRef } from 'react'
import {
  TableContainer,
  Table,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
  Typography,
  Paper,
  Box,
  Button,
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import useExport from './useExport'
import useIsHorizontalOverflow from 'hooks/useIsHorizontalOverflow'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'

export type TableColumn<R> = {
  key: string
  title?: React.ReactNode
  render?: (row: R) => React.ReactNode
  renderExport?: (row: R) => string
  color?: string | ((row: R) => string | undefined)
}

export type TableElementProps<R> = {
  columns: TableColumn<R>[]
  rows: R[]
  getRowId?: (row: R) => string
  hidden?: string[]
  title?: string | null | undefined
}

const TableElement = <R extends { [key: string]: any }>({
  columns,
  rows,
  hidden = [],
  getRowId,
  title,
}: TableElementProps<R>): JSX.Element => {
  const tableContainerRef = useRef<HTMLDivElement | null>(null)
  const tableRef = useRef<HTMLTableElement | null>(null)
  const exportTable = useExport({ title })

  const handleExport = useCallback(() => {
    if (tableRef.current) exportTable(tableRef.current)
  }, [exportTable])

  const shownColumns = useMemo(
    () => columns.filter((c) => !hidden.includes(c.key)),
    [columns, hidden]
  )

  const isOverflow = useIsHorizontalOverflow(tableContainerRef, [columns])

  return (
    <Paper>
      <Box
        sx={{
          p: 2,
          backgroundColor: 'grey.100',
          borderBottom: 1,
          borderBottomColor: 'grey.300',
          display: 'flex',
        }}
      >
        <Box sx={{ flexGrow: 1 }}>
          {/* Maybe subgroup control goes here? */}
        </Box>
        <Button startIcon={<DownloadIcon />} onClick={handleExport}>
          Export
        </Button>
      </Box>
      <Box sx={{ position: 'relative' }}>
        <TableContainer ref={tableContainerRef}>
          <Table ref={tableRef}>
            <TableHead>
              <TableRow>
                {shownColumns.map(({ key, title }, columnIndex) => (
                  <TableCell
                    key={key}
                    sx={
                      columnIndex === 0
                        ? {
                            minWidth: '240px',
                            position: 'sticky',
                            left: 0,
                            backgroundColor: 'white',
                          }
                        : undefined
                    }
                  >
                    <strong>{title || key}</strong>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, i) => {
                const rowId = getRowId ? getRowId(row) : i
                return (
                  <TableRow key={rowId}>
                    {shownColumns.map(({ key, render, color }, columnIndex) => (
                      <TableCell
                        key={`${rowId}:${key}`}
                        sx={[
                          {
                            backgroundColor: color
                              ? typeof color === 'string'
                                ? color
                                : color(row)
                              : undefined,
                            minWidth: '125px',
                          },
                          columnIndex === 0 && {
                            minWidth: '240px',
                            position: 'sticky',
                            left: 0,
                            backgroundColor: 'white',
                          },
                        ]}
                      >
                        {render ? render(row) : row[key]}
                      </TableCell>
                    ))}
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </TableContainer>
        {isOverflow && (
          <Box
            sx={{
              backgroundImage:
                'linear-gradient(to right, rgba(255, 255, 255, 0), white)',
              position: 'absolute',
              top: 0,
              bottom: 0,
              right: 0,
              width: 62,
            }}
          ></Box>
        )}
      </Box>
      {isOverflow && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            padding: 2,
            gap: 1,
            color: 'grey.600',
          }}
        >
          <Typography>Scroll table to see additional columns</Typography>
          <ArrowForwardIcon />
        </Box>
      )}
    </Paper>
  )
}

export default TableElement
